import React from "react"

import { faCheck, faMinus } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { DocumentArrowDownIcon } from "@heroicons/react/24/solid"
import Tippy from "@tippyjs/react"

import { H3, StyledLink } from "~tailwindui/Basics"
import { ratingIcon } from "~tailwindui/helpers/classNameHelpers"

import ordered from "src/ordered"

import CollapsibleContainer from "components/CollapsibleContainer"
import LocalTime from "components/LocalTime"
import { path } from "components/Tutor/TutorRoutes"

import "tippy.js/dist/tippy.css"

const CollapsiblePlan = ({ lessonPlanAssignment }) => {
  const { lessonPlan } = lessonPlanAssignment

  return (
    <div>
      <H3 className="font-semibold">{lessonPlan.name}</H3>
      <ul className="my-0 inline-block text-left">
        {lessonPlan.lessonTopics.map(lessonTopic => (
          <React.Fragment key={lessonTopic.id}>
            <li>
              <CollapsibleContainer
                title={
                  <h2 className="ml-3 inline-block">{lessonTopic.name}</h2>
                }
              >
                <ul>
                  {lessonTopic.lessonResources.map(lessonResource => (
                    <li key={lessonResource.id} className="ml-5 mb-3 pl-3">
                      <div className="space-x-2">
                        {lessonResource.completed ? (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="text-green-600"
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faMinus}
                            className="text-orange-500"
                          />
                        )}
                        <span>{lessonResource.name}</span>

                        {lessonResource.current && (
                          <span className="ml-3 rounded-sm bg-emerald-400 px-2 py-1 text-white">
                            Current
                          </span>
                        )}
                      </div>

                      <div className="ml-5 mt-2">
                        <div className="my-1 flex flex-col gap-4 sm:flex-row">
                          <a
                            href={lessonResource.file.downloadUrl}
                            className="flex cursor-pointer items-center gap-2 font-medium text-sky-700 hover:font-bold"
                          >
                            <DocumentArrowDownIcon className="h-5 w-5" />
                            <span>Download {lessonResource.file.filename}</span>
                          </a>

                          <Tippy
                            content={
                              <img
                                src={lessonResource.file.previewUrl}
                                alt={lessonResource.name}
                                className="mh-[80vh]"
                              />
                            }
                            placement="auto"
                            maxWidth="90vw"
                          >
                            <div>
                              <img
                                src={lessonResource.file.thumbUrl}
                                alt={lessonResource.name}
                              />
                            </div>
                          </Tippy>
                        </div>

                        <ul>
                          {lessonResource.lessonResourceCompletions &&
                            ordered(
                              lessonResource.lessonResourceCompletions,
                              "session",
                              "startsAt"
                            ).map(completion => (
                              <li key={completion.id} className="space-y-3">
                                <span>
                                  Completed during{" "}
                                  <StyledLink
                                    to={path("sessionDetails", {
                                      id: completion.session.id,
                                    })}
                                  >
                                    session with{" "}
                                    {completion.session.tutor?.fullName} on{" "}
                                    <LocalTime
                                      timestamp={completion.session.startsAt}
                                      omitTime
                                    />
                                  </StyledLink>
                                </span>

                                <ul className="mb-4 list-none">
                                  {completion.studentMastery && (
                                    <li className="flex items-center gap-2">
                                      <span>Student-Perceived Mastery</span>
                                      {ratingIcon(completion.studentMastery)}
                                    </li>
                                  )}
                                  {completion.studentTopicMasteryStart && (
                                    <li className="flex items-center gap-2">
                                      <span>
                                        Student-Perceived Mastery at Topic Start
                                      </span>
                                      {ratingIcon(
                                        completion.studentTopicMasteryStart
                                      )}
                                    </li>
                                  )}
                                  {completion.studentTopicMasteryEnd && (
                                    <li className="flex items-center gap-2">
                                      <span>
                                        Student-Perceived Mastery at Topic End
                                      </span>
                                      {ratingIcon(
                                        completion.studentTopicMasteryEnd
                                      )}
                                    </li>
                                  )}
                                  {completion.tutorMastery && (
                                    <li className="flex items-center gap-2">
                                      <span>Tutor-Perceived Mastery</span>
                                      {ratingIcon(completion.tutorMastery)}
                                    </li>
                                  )}
                                  {completion.tutorTopicMastery && (
                                    <li className="flex items-center gap-2">
                                      <span>Tutor-Perceived Topic Mastery</span>
                                      {ratingIcon(completion.tutorTopicMastery)}
                                    </li>
                                  )}
                                </ul>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </li>
                  ))}
                </ul>
              </CollapsibleContainer>
            </li>
          </React.Fragment>
        ))}
      </ul>
      <hr className="my-4" />
    </div>
  )
}

export default CollapsiblePlan
